import { CumulativeProjectDailyStats } from '../../../../../pages/projects/preview/models/CumulativeProjectDailyStats';
import { ProjectDailyStatsResponse } from '../../../../models/projects/stats/ProjectDailyStatsResponse';
import { client } from '../../../../services/apiClient';
import { ChatType } from '../../../../types/monitor/ChatType';
import { getDefaultRange } from '../../../../utils/utils';
import { format } from 'date-fns';
import {
  AllBrandsStatsResult,
  ProjectSovPoint,
  ProjectVisibilityPerMonitorPoint,
  ProjectVisibilityPoint,
} from '../../../../models/projects/stats/AllBrandsStatsResponse';
import { MonitorPeriod } from '../../../../types/monitor/MonitorPeriod';

const PROJECT_DAILY_STATS_ENDPOINT = '/projects/daily-stats';

export type ExportProjectBrandScoreRequest = {
  projectId: number;
  period: MonitorPeriod;
  chatType: ChatType;
  startDate: string;
  endDate: string;
};

export const getProjectCumulativeDailyStats = async (
  cumulativeProjectDailyStats: CumulativeProjectDailyStats | null
): Promise<ProjectDailyStatsResponse> => {
  if (!cumulativeProjectDailyStats) {
    return {} as ProjectDailyStatsResponse;
  }

  const { data } = await client(true, true).post<ProjectDailyStatsResponse>(
    PROJECT_DAILY_STATS_ENDPOINT,
    cumulativeProjectDailyStats
  );
  return data;
};

export const getProjectAllBrandsStats = async (
  id: number,
  chatType: ChatType,
  period: MonitorPeriod,
  from?: Date | null,
  to?: Date | null,
  filterChatTypes?: string[],
  monitorPromptIds?: number[]
): Promise<AllBrandsStatsResult> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const modelsParam = filterChatTypes && filterChatTypes.length > 0
    ? `?models=${filterChatTypes.join(',')}`
    : '';

  const promptIdsParam = monitorPromptIds && monitorPromptIds.length > 0
    ? `${modelsParam ? '&' : '?'}promptIds=${monitorPromptIds.join(',')}`
    : '';

  const { data } = await client(true, true).get<AllBrandsStatsResult>(
    `${PROJECT_DAILY_STATS_ENDPOINT}/all-brands-stats/${id}/${chatType}/${period}/${formattedFrom}/${formattedTo}${modelsParam}${promptIdsParam}`
  );
  return data;
};

export const getProjectVisibilityChart = async (
  id: number,
  chatType: ChatType,
  period: MonitorPeriod,
  from?: Date | null,
  to?: Date | null,
  filterChatTypes?: string[],
  monitorPromptIds?: number[]
): Promise<ProjectVisibilityPoint[]> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const modelsParam =
    filterChatTypes && filterChatTypes.length > 0 ? `?models=${filterChatTypes.join(',')}` : '';

  const promptIdsParam =
    monitorPromptIds && monitorPromptIds.length > 0
      ? `${modelsParam ? '&' : '?'}promptIds=${monitorPromptIds.join(',')}`
      : '';

  const { data } = await client(true, true).get<ProjectVisibilityPoint[]>(
    `${PROJECT_DAILY_STATS_ENDPOINT}/visibility-chart/${id}/${chatType}/${period}/${formattedFrom}/${formattedTo}${modelsParam}${promptIdsParam}`
  );
  return data;
};

export const getProjectVisibilityPerMonitor = async (
  id: number,
  chatType: ChatType,
  from?: Date | null,
  to?: Date | null,
  filterChatTypes?: string[],
  monitorPromptIds?: number[]
): Promise<ProjectVisibilityPerMonitorPoint[]> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const modelsParam =
    filterChatTypes && filterChatTypes.length > 0 ? `?models=${filterChatTypes.join(',')}` : '';

  const promptIdsParam =
    monitorPromptIds && monitorPromptIds.length > 0
      ? `${modelsParam ? '&' : '?'}promptIds=${monitorPromptIds.join(',')}`
      : '';

  const { data } = await client(true, true).get<ProjectVisibilityPerMonitorPoint[]>(
    `${PROJECT_DAILY_STATS_ENDPOINT}/visibility-per-monitor/${id}/${chatType}/${formattedFrom}/${formattedTo}${modelsParam}${promptIdsParam}`
  );
  return data;
};

export const getProjectSovTimeSeries = async (
  id: number,
  chatType: ChatType,
  period: MonitorPeriod,
  from?: Date | null,
  to?: Date | null,
  filterChatTypes?: string[],
  monitorPromptIds?: number[]
): Promise<ProjectSovPoint[]> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  const modelsParam =
    filterChatTypes && filterChatTypes.length > 0 ? `?models=${filterChatTypes.join(',')}` : '';

  const promptIdsParam =
    monitorPromptIds && monitorPromptIds.length > 0
      ? `${modelsParam ? '&' : '?'}promptIds=${monitorPromptIds.join(',')}`
      : '';

  const { data } = await client(true, true).get<ProjectSovPoint[]>(
    `${PROJECT_DAILY_STATS_ENDPOINT}/sov-time-series/${id}/${chatType}/${period}/${formattedFrom}/${formattedTo}${modelsParam}${promptIdsParam}`
  );
  return data;
};

export const exportProjectStats = async (
  projectId: number,
  chatType: ChatType,
  from?: Date | null,
  to?: Date | null
): Promise<Blob> => {
  const defaultRange = getDefaultRange();
  from = from ? new Date(from) : defaultRange[0];
  to = to ? new Date(to) : defaultRange[1];

  const formattedFrom = format(from, 'yyyy-MM-dd');
  const formattedTo = format(to, 'yyyy-MM-dd');

  return client(true, true)
    .get<Blob>(
      `${PROJECT_DAILY_STATS_ENDPOINT}/export-project-stats/${projectId}/${formattedFrom}/${formattedTo}/${chatType}`,
      {
        responseType: 'blob',
      }
    )
    .then((response) => response.data)
    .catch((error) => error);
};

export const exportProjectBrandScore = async (
  exportProjectBrandScoreRequest: ExportProjectBrandScoreRequest | null
): Promise<Blob> => {
  if (!exportProjectBrandScoreRequest) {
    throw Error('Invalid data');
  }

  const { projectId, period, chatType, startDate, endDate } = exportProjectBrandScoreRequest;
  const { data } = await client(true, true).get<Blob>(
    `${PROJECT_DAILY_STATS_ENDPOINT}/export-brand-score/${projectId}/${startDate}/${endDate}/${chatType}/${period}`,
    {
      responseType: 'blob',
    }
  );

  return data;
};
